/**
 * Which shortcuts the operating system actually gave us.
 *
 * Registering a combo system-wide can fail - another program already owns it,
 * or it has no modifier and we refuse to take a bare key away from every app
 * on the machine. Either way the binding still works while Pugo is focused,
 * so nothing looks broken until you press it in a game and nothing happens.
 *
 * App does the registering; the keybinds screen reads the result from here,
 * so a failure is a label on the row rather than silence.
 */

import { useSyncExternalStore } from 'react';

let failed: string[] = [];
const listeners = new Set<() => void>();

/** Whether shortcuts are watched (other apps still get the key) or claimed. */
let passthrough = true;

export function setHotkeyMode(next: boolean): void {
  if (passthrough === next) return;
  passthrough = next;
  for (const fn of listeners) fn();
}

export function useHotkeyPassthrough(): boolean {
  return useSyncExternalStore(subscribe, () => passthrough, () => passthrough);
}

export function setHotkeyFailures(next: string[]): void {
  // Same set, same array: useSyncExternalStore compares by identity and would
  // otherwise re-render every listener on every settings save.
  if (next.length === failed.length && next.every((id, i) => failed[i] === id)) return;
  failed = next;
  for (const fn of listeners) fn();
}

function subscribe(fn: () => void): () => void {
  listeners.add(fn);
  return () => { listeners.delete(fn); };
}

/*
 * The last shortcut the operating system delivered.
 *
 * Recorded so the keybinds screen can prove the system-wide path is alive.
 * Whether a global hotkey works is otherwise invisible: you press it in a
 * game, nothing appears to happen, and there is no way to tell a shortcut
 * that never registered from one that fired and did something silent.
 */
let lastFired: { action: string; at: number } | null = null;
const firedListeners = new Set<() => void>();

export function noteHotkeyFired(action: string): void {
  lastFired = { action, at: Date.now() };
  for (const fn of firedListeners) fn();
}

export function useLastHotkey(): { action: string; at: number } | null {
  return useSyncExternalStore(
    (fn) => { firedListeners.add(fn); return () => { firedListeners.delete(fn); }; },
    () => lastFired,
    () => lastFired
  );
}

/** Action ids that could not be claimed system-wide. */
export function useHotkeyFailures(): string[] {
  return useSyncExternalStore(subscribe, () => failed, () => failed);
}

/** The same list, for code that is not a component. */
export function hotkeyFailures(): string[] {
  return failed;
}
